import React, { useState } from "react";
import ReactFlow, {
  addEdge,
  Controls,
  MiniMap,
  Handle,
  useEdgesState,
  useNodesState,
  ReactFlowProvider,
} from "react-flow-renderer";
import {
  Box,
  Typography,
  Button,
  Switch,
  Modal,
  TextField,
  List,
  ListItem,
  ListItemButton,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import RouterIcon from "@mui/icons-material/Route";
import InboxIcon from "@mui/icons-material/Inbox";
import HubIcon from "@mui/icons-material/Hub";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const CustomNode = ({ data }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      width: 100,
      cursor: "pointer",
      position: "relative",
    }}
    onClick={data.onClick} // Add click handler for the node
  >
    <Handle
      type="source"
      position="right"
      style={{ background: "#555", top: "50%", transform: "translateY(-50%)" }}
    />
    <Handle
      type="target"
      position="left"
      style={{ background: "#555", top: "50%", transform: "translateY(-50%)" }}
    />
    <Box
      sx={{
        backgroundColor: data.color || "#EFF4FB",
        borderRadius: "50%",
        height: 60,
        width: 60,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        marginBottom: 1,
      }}
    >
      {data.icon || <AddCircleOutlineIcon />}
    </Box>
    <Typography variant="caption" sx={{ fontWeight: "bold", color: "#333" }}>
      {data.label || "Module"}
    </Typography>
  </Box>
);

const nodeTypes = {
  custom: CustomNode,
};

const integrations = [
  { name: "Airtable", icon: <InboxIcon sx={{ color: "blue", fontSize: 40 }} /> },
  { name: "HubSpot", icon: <HubIcon sx={{ color: "orange", fontSize: 40 }} /> },
];

const ScenarioBuilderComponent = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([
    {
      id: "1",
      type: "custom",
      data: {
        label: "Start Node",
        color: "#A0E7E5",
        onClick: () => handleNodeClick("1"),
      },
      position: { x: 250, y: 100 },
    },
  ]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [logs, setLogs] = useState([]);
  const [logsVisible, setLogsVisible] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [apiDetails, setApiDetails] = useState({ header: "", body: "" });

  const onConnect = (params) => {
    setEdges((eds) =>
      addEdge({ ...params, animated: true, style: { stroke: "#555" } }, eds)
    );
    setLogs((logs) => [...logs, `Edge connected from ${params.source} to ${params.target}`]);
  };

  const handleNodeClick = (nodeId) => {
    setSelectedNodeId(nodeId);
    setShowIntegrationModal(true);
  };

  const handleAddRouter = () => {
    const id = `${nodes.length + 1}`;
    const newNode = {
      id,
      type: "custom",
      data: {
        label: "",
        icon: <AddCircleOutlineIcon />,
        color: "#FFD6E0",
        onClick: () => handleNodeClick(id),
      },
      position: { x: Math.random() * 400, y: Math.random() * 400 },
    };
    setNodes((nds) => [...nds, newNode]);
    setLogs((logs) => [...logs, `Router Node ${id} added.`]);
  };

  const handleIntegrationSelect = (integration) => {
    setSelectedIntegration(integration);
    setShowIntegrationModal(false);
    setShowInputModal(true);
  };

  const handleApiDetailChange = (e) => {
    const { name, value } = e.target;
    setApiDetails((prev) => ({ ...prev, [name]: value }));
  };

  const saveNodeDetails = () => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNodeId
          ? {
              ...node,
              data: {
                ...node.data,
                label: `${selectedIntegration.name}`,
                icon: selectedIntegration.icon,
              },
            }
          : node
      )
    );
    setLogs((logs) => [
      ...logs,
      `Node ${selectedNodeId} configured with ${selectedIntegration.name} and API details.`,
    ]);
    setShowInputModal(false);
    setApiDetails({ header: "", body: "" });
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          backgroundColor: "#F8F9FA",
        }}
      >
        <Button startIcon={<PlayArrowIcon />} variant="contained">
          Run Once
        </Button>
        <Switch defaultChecked />
        <Button startIcon={<SaveIcon />} onClick={() => setLogs([...logs, "Scenario saved."])}>
          Save
        </Button>
        <Button startIcon={<RouterIcon />} onClick={handleAddRouter}>
          Add Router
        </Button>
      </Box>

      {/* Main Canvas */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: "#f0f0f0",
          position: "relative",
          border: "1px solid #ccc",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
        >
          <Controls />
          <MiniMap />
        </ReactFlow>
      </Box>

      {/* Logs Toggle */}
      <Box
        sx={{
          padding: "8px",
          backgroundColor: "#F8F9FA",
          borderTop: "1px solid #ccc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle2">Logs:</Typography>
        <IconButton onClick={() => setLogsVisible((prev) => !prev)}>
          {logsVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Logs Section */}
      {logsVisible && (
        <Box
          sx={{
            padding: "8px",
            backgroundColor: "#EDEDED",
            maxHeight: "150px",
            overflowY: "auto",
          }}
        >
          {logs.map((log, index) => (
            <Typography key={index} variant="body2">
              {log}
            </Typography>
          ))}
        </Box>
      )}

      {/* Integration Modal */}
      <Modal
        open={showIntegrationModal}
        onClose={() => setShowIntegrationModal(false)}
        aria-labelledby="integration-modal"
        aria-describedby="integration-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="integration-modal-title" variant="h6" sx={{ mb: 2 }}>
            Select an Integration
          </Typography>
          <List>
            {integrations.map((integration) => (
              <ListItem key={integration.name}>
                <ListItemButton onClick={() => handleIntegrationSelect(integration)}>
                  {integration.icon}
                  <Typography sx={{ ml: 2 }}>{integration.name}</Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>

      {/* Input Modal */}
      <Modal
        open={showInputModal}
        onClose={() => setShowInputModal(false)}
        aria-labelledby="input-modal"
        aria-describedby="input-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="input-modal-title" variant="h6" sx={{ mb: 2 }}>
            Configure API Details
          </Typography>
          <TextField
            fullWidth
            label="Header"
            name="header"
            value={apiDetails.header}
            onChange={handleApiDetailChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Body"
            name="body"
            value={apiDetails.body}
            onChange={handleApiDetailChange}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={saveNodeDetails}>
            Save
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

// Wrap with ReactFlowProvider
const ScenarioBuilder = () => (
  <ReactFlowProvider>
    <ScenarioBuilderComponent />
  </ReactFlowProvider>
);

export default ScenarioBuilder;